/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useCallback, useRef } from 'react';
import { clickOnImage, changeLayoutId, onDragEndImage } from 'actions/scene';
import { useDispatch } from 'react-redux';
import { useDrag } from 'react-dnd';
import ProgressBar from 'components/common/ProgressBar';
import Grid from 'components/common/NewGrid';
import classnames from 'classnames';
import { find } from 'lodash';
import store from 'store';
import { useSetIsEditing } from 'components/editors/NewEditor/hooks/useIsEditing';
import { modalTypes, modes, shapeTypes } from 'constants/index';
import { close } from 'actions/modal';
import PixaPrints from 'api';
import { openModal, storeGetState } from 'utils/utils';
import SceneHelper from 'utils/SceneHelper';
import { getPrintLimitMaxMinAndObjectsLengthSync, idMap } from '../utils';

const FACE_API_SCRIPT_ID = 'face-api-runtime-script';
const FACE_API_SCRIPT_SRC = '/vendors/face-api.js';
let faceApiLoadPromise = null;
let tinyFaceDetectorLoadPromise = null;

const loadFaceApi = () => {
  if (window.faceapi) return Promise.resolve(window.faceapi);
  if (faceApiLoadPromise) return faceApiLoadPromise;

  faceApiLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(FACE_API_SCRIPT_ID);
    if (existingScript) {
      if (window.faceapi) {
        resolve(window.faceapi);
        return;
      }
      existingScript.addEventListener('load', () => resolve(window.faceapi), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load face-api script')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = FACE_API_SCRIPT_ID;
    script.src = FACE_API_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (!window.faceapi) {
        reject(new Error('faceapi is not available on window after script load'));
        return;
      }
      resolve(window.faceapi);
    };
    script.onerror = () => reject(new Error(`Failed to load ${FACE_API_SCRIPT_SRC}`));
    document.body.appendChild(script);
  });

  return faceApiLoadPromise;
};

const getFaceApi = async () => {
  const faceapi = await loadFaceApi();
  if (!tinyFaceDetectorLoadPromise) {
    tinyFaceDetectorLoadPromise = faceapi.nets.tinyFaceDetector.loadFromUri('/models');
  }
  await tinyFaceDetectorLoadPromise;
  return faceapi;
};

const detectFaces = async (originalSrc) => {
  try {
    const faceapi = await getFaceApi();
    const img = await faceapi.fetchImage(originalSrc);
    return (await faceapi.detectAllFaces(img, new faceapi.TinyFaceDetectorOptions())).map(({ box }) => ({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
    }));
  } catch (error) {
    console.warn('[face-detection] Failed to detect faces, fallback to empty detections.', error);
    return [];
  }
};

const onDragEnd = (photo, id, objects, dispatch) => {
  let imageId = photo.id;
  if (idMap[imageId]) {
    imageId = idMap[imageId];
  }
  const newPhoto = find(storeGetState().photos.photos, { id: imageId });
  dispatch(onDragEndImage({ id, image: newPhoto }));
};

const handleClickOnImage = async (_image, { onClick, dispatch }) => {
  const shapes = SceneHelper.getCurrentPageShapes(storeGetState(), [shapeTypes.dropZone, shapeTypes.layout]);
  const nextClickOnImage = async () => {
    const mode = SceneHelper.getMode(storeGetState());
    const faceDetections = await detectFaces(_image.originalSrc);

    const image = { ..._image, faceDetections };
    if (shapes.length === 0 && mode === modes.canvas) {
      const addPhotoContainer =
        ({ withFrame }) =>
        () => {
          store.dispatch(changeLayoutId({ id: 'default', withFrame }));
          dispatch(clickOnImage({ image }));
          PixaPrints.saveState({ autosave: true });
          store.dispatch(close());
        };
      openModal({
        type: modalTypes.confirm,
        modalProps: {
          onConfirm: addPhotoContainer({ withFrame: false }),
          onClose: addPhotoContainer({ withFrame: true }),
          header: 'Add layout',
          text: 'Please select where to place the layout',
          confirmText: 'Colour edges',
          cancelText: 'Image on edges',
        },
      });
    } else if (shapes.length === 0 && mode === modes.photoPrint) {
      store.dispatch(changeLayoutId({ id: 'default' }));
      await dispatch(clickOnImage({ image }));
    } else {
      await dispatch(clickOnImage({ image }));
    }
    onClick && onClick();
    PixaPrints.saveState({ autosave: true });
  };
  nextClickOnImage();
  // if (!countImages) {
  //   openModal({
  //     type: modalTypes.confirm,
  //     modalProps: {
  //       onConfirm: () => {
  //         store.dispatch(close());
  //       },
  //       header: ' Before You Insert Your Image',
  //       text: `Please check that your image fits well in the template. After inserting, parts of your image may be cropped or positioned incorrectly. Use the Crop tab to adjust it and make sure nothing important is cut off.`,
  //       confirmText: 'Confirm',
  //       hideCancelButton: true,
  //     },
  //   });
  // }
};

const draggableDivStyle = { height: '100%' };
const Photo = React.memo(
  ({
    id,
    src,
    thumbnailSrc,
    progress,
    uploaded,
    alwaysShowWhiteRectangle,
    isActive,
    style,
    onClick,
    photo,
    ...rest
  }) => {
    const setIsEditing = useSetIsEditing();
    const dispatch = useDispatch();
    const [{ opacity }, dragRef] = useDrag({
      type: 'image',
      item: () => {
        setIsEditing(false);
        return { type: 'image', photo };
      },
      end: (item, monitor) => {
        const dropResult = monitor.getDropResult();
        if (item && dropResult) {
          setIsEditing(false);
          const { objects } = storeGetState().scene;
          onDragEnd(item.photo, dropResult.name, objects, dispatch);
        }
      },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
      }),
    });

    const hasDoubleClick = useRef(false);

    const handleClick = useCallback(() => {
      if (hasDoubleClick.current) return;
      hasDoubleClick.current = true;
      const {
        scene,
        scene: {
          config: { layouts, layoutId, mode },
        },
      } = storeGetState();
      if (mode === modes.photoPrint) {
        const { printLimitMax, photoCount } = getPrintLimitMaxMinAndObjectsLengthSync({ scene });
        const isMultiAperture = ((find(layouts, { id: layoutId }) || {}).layout || []).length > 1;
        if (printLimitMax > photoCount) {
          !isMultiAperture && handleClickOnImage({ id, src, uploaded, ...rest }, { onClick, dispatch });
        } else {
          const message = 'Print limit reached for this product';
          openModal({ type: modalTypes.warning, modalProps: { message } });
        }
      } else {
        handleClickOnImage({ id, src, uploaded, ...rest }, { onClick, dispatch });
      }
      setTimeout(() => {
        hasDoubleClick.current = false;
      }, 500);
    }, [dispatch, id, onClick, rest, src, uploaded]);
    return (
      <Grid.Item key={id}>
        <div
          className={classnames({ checked: isActive }, 'cursor-pointer')}
          ref={dragRef}
          draggable
          style={draggableDivStyle}
        >
          <img alt={id} src={thumbnailSrc} opacity={opacity} onClick={handleClick} loading="lazy" />
          <ProgressBar
            alwaysShowWhiteRectangle={alwaysShowWhiteRectangle}
            uploaded={uploaded}
            value={typeof progress === 'number' ? progress : 0}
          />
        </div>
      </Grid.Item>
    );
  },
);

export default Photo;
